import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Store } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useUser, useProducts } from '../hooks/useQueries';
import { ProductCard } from '../components/ProductCard';
import { SocialLink } from '../components/SocialLink';

export function PreviewPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: profile, isLoading: profileLoading } = useUser(user?.id || '');
  const { data: products = [], isLoading: productsLoading } = useProducts(user?.id || '');

  if (profileLoading || productsLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading preview...</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Profile not found</p>
        </div>
      </div>
    );
  }

  const publicUrl = `${window.location.origin}/u/${profile.username}`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50">
      {/* Header with navigation */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </button>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-500">Preview Mode</span>
              <a
                href={publicUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center px-3 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-lg text-sm transition duration-300"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Open Public Page
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Preview Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          {/* Profile Header with Gradient */}
          <div className="h-32 bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-12">
            <div className="text-center">
              {profile.avatar ? (
                <img
                  src={profile.avatar}
                  alt={profile.displayName}
                  className="w-24 h-24 rounded-full mx-auto mb-4 border-4 border-white shadow-lg"
                />
              ) : (
                <div className="w-24 h-24 bg-white rounded-full mx-auto mb-4 flex items-center justify-center shadow-lg border-4 border-white">
                  <span className="text-2xl font-bold text-blue-600">
                    {profile.displayName.charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
              <h1 className="text-2xl font-bold text-white mb-2">{profile.displayName}</h1>
              <p className="text-blue-100">@{profile.username}</p>
            </div>
          </div>

          {/* Bio */}
          {profile.bio && (
            <div className="px-6 py-6 border-b border-gray-200">
              <p className="text-gray-700 text-center leading-relaxed">{profile.bio}</p>
            </div>
          )}

          {/* Shop Button */}
          <div className="px-6 py-6 border-b border-gray-200">
            <button
              onClick={() => window.open(publicUrl + '/shop', '_blank')}
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white py-4 px-6 rounded-lg font-semibold shadow-sm transition duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              <div className="flex items-center justify-center gap-3">
                <Store className="w-5 h-5" />
                <span>Shop Collection</span>
              </div>
            </button>
          </div>

          {/* Social Links */}
          {profile.socialLinks && profile.socialLinks.length > 0 && (
            <div className="px-6 py-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 text-center">Connect with me</h2>
              <div className="flex flex-wrap justify-center gap-3">
                {profile.socialLinks
                  .filter(link => link.isActive)
                  .map((link) => (
                    <SocialLink
                      key={link.id}
                      platform={link.platform}
                      url={link.url}
                      title={link.title}
                      onClick={() => {}} // Preview mode - no tracking
                    />
                  ))}
              </div>
            </div>
          )}

          {/* Products */}
          {products.length > 0 && (
            <div className="px-6 py-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">My Products</h2>
                <span className="text-sm text-gray-500">{products.length} products</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {products.map((product) => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    onAddToCart={() => {}} // Preview mode - no cart functionality
                    onProductClick={() => {}} // Preview mode - no tracking
                    showCartButton={!product.isAffiliate} // Hide cart for affiliate products
                  />
                ))}
              </div>
            </div>
          )}

          {/* Empty state for products */}
          {products.length === 0 && (
            <div className="px-6 py-12 text-center">
              <div className="text-gray-400 mb-4">
                <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No products yet</h3>
              <p className="text-gray-600 mb-4">Add some products to showcase your offerings</p>
              <button
                onClick={() => navigate('/dashboard/products/new')}
                className="px-6 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-lg transition duration-300"
              >
                Add Your First Product
              </button>
            </div>
          )}
        </div>

        {/* Preview Tips */}
        <div className="mt-8 bg-blue-50 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-3">Preview Tips</h3>
          <ul className="space-y-2 text-blue-800">
            <li className="flex items-start">
              <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
              This is how your public profile will appear to visitors
            </li>
            <li className="flex items-start">
              <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
              Affiliate products won't show cart buttons - visitors will be redirected to the affiliate link
            </li>
            <li className="flex items-start">
              <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
              Share your public URL: <code className="bg-white px-2 py-1 rounded text-sm ml-1">{publicUrl}</code>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}
